import { set, getOr, flow, reduce, some } from 'lodash/fp';
import createReducer from '../../../../../helpers/createReducer';
import {
  createReservationOrders,
  updateReservationOrders,
  fetchReservationPicklists,
  fetchInventoryItemAvailabilityByLocation,
  fetchInventoryPackageAvailabilityByLocation
} from '../../../sagas/routines';
import {
  onEditOrders,
  clearOrders,
  clearPicklists,
  updateItemsSumQuantities,
  updateItemsInitialSumQuantities
} from '../../../actions/routes/orders';
import toOrdersByPicklist from '../../../../../helpers/toOrdersByPicklist';
import getItemAvailabilityUniqId from '../../../../../helpers/getItemAvailabilityUniqId';

const initialState = {
  picklists: [],
  ordersByPicklist: {},
  itemsAvailability: {},
  itemsSumQuantityByUniqKey: {},
  itemsInitialSumQuantityByUniqKey: {},
  hasLinkedPicklistItems: false,
  fetchingPicklists: false,
  fetchedPicklists: false,
  savingOrders: false,
  savedOrders: false,
  fetchingInventoryAvailability: false,
  fetchedInventoryAvailability: false
};

const ordersToItemsSumQuantityByUniqKey = flow(
  reduce((allItems, each) => [...allItems, ...each.items], []),
  reduce((sumByUniqKey, { inventorySnapshot, quantity }) => {
    if (!inventorySnapshot) return sumByUniqKey;
    const uniqKey = getItemAvailabilityUniqId(inventorySnapshot);
    const sumQuantity = getOr(0, uniqKey, sumByUniqKey) + quantity;
    return set(uniqKey, sumQuantity, sumByUniqKey);
  }, {})
);

const fetchPicklistsHandler = routine => ({
  [routine.TRIGGER]: state => ({
    ...state,
    fetchingPicklists: true,
    fetchedPicklists: false
  }),
  [routine.SUCCESS]: (state, { payload: { response } }) => {
    const picklists = getOr([], 'items', response);
    const hasLinkedPicklistItems = flow(
      reduce((allItems, each) => [...allItems, ...each.items], []),
      some('inventoryItemId')
    )(picklists);
    return { ...state, picklists, hasLinkedPicklistItems };
  },
  [routine.FULFILL]: state => ({
    ...state,
    fetchingPicklists: false,
    fetchedPicklists: true
  })
});

const updateReservationOrdersHandler = routine => ({
  [routine.TRIGGER]: state => ({
    ...state,
    savingOrders: true,
    savedOrders: false
  }),
  [routine.SUCCESS]: (state, { payload: { response } }) =>
    set('ordersByPicklist', toOrdersByPicklist(response.items), state),
  [routine.FULFILL]: state => ({
    ...state,
    savingOrders: false,
    savedOrders: true
  })
});

const fetchInventoryAvailabilityHandler = routine => ({
  [routine.TRIGGER]: state => ({
    ...state,
    fetchingInventoryAvailability: true,
    fetchedInventoryAvailability: false
  }),
  [routine.SUCCESS]: (state, { payload: { response } }) =>
    set(
      `itemsAvailability.${getItemAvailabilityUniqId(response)}`,
      response,
      state
    ),
  [routine.FULFILL]: state => ({
    ...state,
    fetchingInventoryAvailability: false,
    fetchedInventoryAvailability: true
  })
});

const onEditOrdersHandlers = action => ({
  [action.toString()]: (state, { payload }) =>
    set('ordersByPicklist', payload, state)
});

const clearOrdersHandler = action => ({
  [action.toString()]: set('ordersByPicklist', {})
});

const clearPicklistsHandler = action => ({
  [action.toString()]: set('picklists', [])
});

const updateItemsSumQuantitiesHandler = action => ({
  [action.toString()]: (state, { payload: updatedOrder }) => {
    const { picklistId } = updatedOrder;
    const orders = set(picklistId, updatedOrder, state.ordersByPicklist);
    return {
      ...state,
      itemsSumQuantityByUniqKey: ordersToItemsSumQuantityByUniqKey(orders)
    };
  }
});

const updateItemsInitialSumQuantitiesHandler = action => ({
  [action.toString()]: (state, { payload: orders }) => ({
    ...state,
    itemsInitialSumQuantityByUniqKey: ordersToItemsSumQuantityByUniqKey(orders)
  })
});

export default createReducer(initialState, {
  ...onEditOrdersHandlers(onEditOrders),
  ...updateReservationOrdersHandler(createReservationOrders),
  ...updateReservationOrdersHandler(updateReservationOrders),
  ...fetchPicklistsHandler(fetchReservationPicklists),
  ...fetchInventoryAvailabilityHandler(
    fetchInventoryItemAvailabilityByLocation
  ),
  ...fetchInventoryAvailabilityHandler(
    fetchInventoryPackageAvailabilityByLocation
  ),
  ...clearOrdersHandler(clearOrders),
  ...clearPicklistsHandler(clearPicklists),
  ...updateItemsSumQuantitiesHandler(updateItemsSumQuantities),
  ...updateItemsInitialSumQuantitiesHandler(updateItemsInitialSumQuantities)
});
